package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.spring
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.layout
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.formatOneDecimal
import de.xtl.retroguide.protocol.MAX_ROTI
import de.xtl.retroguide.protocol.RadarDimension
import de.xtl.retroguide.protocol.VisibleAnswer
import de.xtl.retroguide.question.emoji
import de.xtl.retroguide.question.label
import de.xtl.retroguide.radarStats
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.Res
import retroguide.app.shared.generated.resources.radar_answer_count
import retroguide.app.shared.generated.resources.radar_answer_single
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin

private const val CHART_RADIUS = .32f
private const val LABEL_RADIUS = .43f

/** Hexagonal radar of the team average (filled) over each person's answer (faint outlines). */
@Composable
internal fun RadarChart(answers: List<VisibleAnswer>) {
    val stats = answers.radarStats() ?: return
    val grow = remember { Animatable(0f) }
    LaunchedEffect(Unit) { grow.animateTo(1f, spring(dampingRatio = .55f, stiffness = 90f)) }
    val primary = MaterialTheme.colorScheme.primary
    val grid = MaterialTheme.colorScheme.outlineVariant
    val individual = MaterialTheme.colorScheme.tertiary
    ElevatedCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            BoxWithConstraints(Modifier.fillMaxWidth().aspectRatio(1.15f)) {
                val width = constraints.maxWidth
                val height = constraints.maxHeight
                Canvas(Modifier.fillMaxSize()) {
                    val center = Offset(size.width / 2, size.height / 2)
                    val radius = size.minDimension * CHART_RADIUS * 1.25f
                    drawGrid(center, radius, grid)
                    stats.answers.forEach { (_, answer) ->
                        drawPolygon(
                            center,
                            radius,
                            RadarDimension.entries.map { answer.scores.getValue(it).toFloat() * grow.value },
                            individual.copy(alpha = .35f),
                            fill = false,
                        )
                    }
                    val averages = RadarDimension.entries.map { stats.averages.getValue(it).toFloat() * grow.value }
                    drawPolygon(center, radius, averages, primary.copy(alpha = .28f), fill = true)
                    drawPolygon(center, radius, averages, primary, fill = false)
                    averages.forEachIndexed { index, value ->
                        drawCircle(primary, size.minDimension * .012f, point(center, radius * value / MAX_ROTI, index))
                    }
                }
                RadarDimension.entries.forEachIndexed { index, dimension ->
                    val angle = axisAngle(index)
                    val labelRadius = minOf(width, height) * LABEL_RADIUS * 1.18f
                    val x = width / 2f + cos(angle) * labelRadius
                    val y = height / 2f + sin(angle) * labelRadius
                    Text(
                        "${dimension.emoji} ${dimension.label()}",
                        style = MaterialTheme.typography.labelLarge,
                        modifier = Modifier.layout { measurable, layoutConstraints ->
                            val placeable = measurable.measure(layoutConstraints.copy(minWidth = 0, minHeight = 0))
                            layout(placeable.width, placeable.height) {
                                placeable.place(
                                    (x - placeable.width / 2f).roundToInt().coerceIn(0, (width - placeable.width).coerceAtLeast(0)),
                                    (y - placeable.height / 2f).roundToInt().coerceIn(0, (height - placeable.height).coerceAtLeast(0)),
                                )
                            }
                        },
                    )
                }
            }
            Text(
                if (stats.answers.size == 1) {
                    stringResource(Res.string.radar_answer_single)
                } else {
                    stringResource(Res.string.radar_answer_count, stats.answers.size)
                },
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                RadarDimension.entries.forEach { dimension ->
                    Surface(shape = RoundedCornerShape(50), color = MaterialTheme.colorScheme.primaryContainer) {
                        Text(
                            "${dimension.emoji} ${dimension.label()} ${stats.averages.getValue(dimension).formatOneDecimal()}",
                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Medium,
                        )
                    }
                }
            }
        }
    }
}

private fun axisAngle(index: Int) = (-90f + index * 360f / RadarDimension.entries.size) * PI.toFloat() / 180f

private fun point(center: Offset, distance: Float, index: Int): Offset {
    val angle = axisAngle(index)
    return center + Offset(cos(angle), sin(angle)) * distance
}

private fun DrawScope.drawGrid(center: Offset, radius: Float, color: Color) {
    (1..MAX_ROTI).forEach { ring ->
        drawPolygon(center, radius, List(RadarDimension.entries.size) { ring.toFloat() }, color, fill = false)
    }
    RadarDimension.entries.indices.forEach { index ->
        drawLine(color, center, point(center, radius, index), strokeWidth = 1.dp.toPx())
    }
}

private fun DrawScope.drawPolygon(center: Offset, radius: Float, values: List<Float>, color: Color, fill: Boolean) {
    val path = Path()
    values.forEachIndexed { index, value ->
        val p = point(center, radius * value / MAX_ROTI, index)
        if (index == 0) path.moveTo(p.x, p.y) else path.lineTo(p.x, p.y)
    }
    path.close()
    if (fill) drawPath(path, color) else drawPath(path, color, style = Stroke(2.dp.toPx()))
}
